import * as React from 'react';
import { useState } from 'react';
import { Autocomplete, Checkbox, FormControlLabel, FormHelperText, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Grid from '@mui/material/Unstable_Grid2';
import User from 'benchmarking-domain/user';
import useApi from 'hooks/use-api';

interface UserFormProps {
  user?: User;
  disableEmail?: boolean;
  onSubmit: (data: User) => Promise<void>;
}

export function UserForm({ user, disableEmail, onSubmit }: UserFormProps): React.JSX.Element {
  const { subscribeUserExists } = useApi();

  const [prevUser, setPrevUser] = useState<User | undefined>(user);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [name, setName] = useState<string>(user?.name ?? '');
  const [email, setEmail] = useState<string>(user?.email ?? '');
  const [roles, setRoles] = useState<string[]>(user?.roles ?? []);
  const [disabled, setDisabled] = useState<boolean | undefined>(user?.disabled ?? false);

  const [emailError, setEmailError] = useState<string | undefined>(undefined);

  if (prevUser !== user) {
    setPrevUser(user);
    setName(user?.name ?? '');
    setEmail(user?.email ?? '');
    setRoles(user?.roles ?? []);
    setDisabled(user?.disabled);
  }

  React.useEffect(() => {
    if (!email || email === user?.email) {
      setEmailError(undefined);
      return;
    }

    const unsubscribeUserExists = subscribeUserExists(email, (exists) =>
      setEmailError(exists && !isSaving ? 'Email is already in use' : undefined)
    );

    return () => {
      unsubscribeUserExists();
    };
  }, [email, user, subscribeUserExists, setEmailError, isSaving]);

  const saveData = async (): Promise<void> => {
    setIsSaving(true);

    try {
      await onSubmit({ ...user, id: user?.id ?? '', email, name, roles, disabled });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        await saveData();
      }}
    >
      <Card>
        <CardHeader subheader="The information can be edited" title={user ? 'User' : 'New user'} />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Name</InputLabel>
                <OutlinedInput
                  onChange={(e) => {
                    setName(e.target.value);
                  }}
                  value={name}
                  label="Name"
                  name="name"
                />
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required error={emailError != undefined}>
                <InputLabel>Email</InputLabel>
                <OutlinedInput
                  type="email"
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  value={email}
                  label="Email"
                  name="email"
                  disabled={disableEmail}
                />
                <FormHelperText>{emailError}</FormHelperText>
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <Autocomplete
                  multiple
                  id="organisations-filled"
                  options={[]}
                  freeSolo
                  autoSelect
                  disabled
                  value={user?.organisations ?? []}
                  renderTags={(value: string[], getTagProps) =>
                    value.map((option: string, index: number) => (
                      <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} variant="outlined" label="Organisations" />}
                />
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControl fullWidth required>
                <Autocomplete
                  multiple
                  id="roles-filled"
                  options={['superAdmin']}
                  filterSelectedOptions
                  value={roles}
                  onChange={(e, value) => {
                    setRoles(value);
                  }}
                  renderTags={(value: string[], getTagProps) =>
                    value.map((option: string, index: number) => (
                      <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
                    ))
                  }
                  renderInput={(params) => <TextField {...params} variant="outlined" label="Roles" />}
                />
              </FormControl>
            </Grid>
            <Grid md={6} xs={12}>
              <FormControlLabel
                control={<Checkbox checked={disabled} onChange={(_, checked) => setDisabled(checked)} />}
                label="Account disabled"
              />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <Button type="submit" variant="contained" disabled={isSaving || emailError != undefined}>
            {user ? 'Save details' : 'Create new user'}
          </Button>
        </CardActions>
      </Card>
    </form>
  );
}
